import type {WorkspaceModelType} from "@stores/WorkspaceStore";
import type {DataSourceManager} from "@stores/dataSources/DataSourceManager";
import type {QuizzesDataSource} from "@stores/dataSources/QuizzesDataSource";
import type {Snapshot} from "../@types/document-addition";

import {isMoreThanNDaysAgo} from "../utils/date/isMoreThanNDaysAgo";
import {SUBSCRIPTION_PLANS} from "../checkout/subscription_plans";
import {SeatUsage} from "../services/SeatManager";


export const FREEMIUM_LIMITS = {
    ItemCount: 50,
    HistoryFreeDays: 7,
    QuizCount: 3,
    AiQuizGenerationCount: 3,
    LocationCount: 1,
}

export enum ActionName {
    CreateItem,
    ViewHistory,
    CreateQuiz,
    InviteUsers,
    AiGenerate,
    CreateTaskListUnit,
}

export interface CreateItemAction {
    actionName: ActionName.CreateItem;
    workspace: WorkspaceModelType;
}

export interface ViewHistoryAction {
    actionName: ActionName.ViewHistory;
    snapshot: Snapshot;
}

export interface CreateQuizAction {
    actionName: ActionName.CreateQuiz;
    quizzesDataSource: QuizzesDataSource;
}

export interface InviteUsersAction {
    actionName: ActionName.InviteUsers;
}

export interface AiGenerateAction {
    actionName: ActionName.AiGenerate;
    currentUsage: number;
}

export interface CreateTaskListUnitAction {
    actionName: ActionName.CreateTaskListUnit;
    seatUsage: SeatUsage;
}

export type ActionToCheckRights = CreateItemAction | ViewHistoryAction | CreateQuizAction | InviteUsersAction | AiGenerateAction | CreateTaskListUnitAction;

export const isAllowed = (dsManager: DataSourceManager, action: ActionToCheckRights) => {
    const clientSubscriptionPlan = dsManager.clientAR?.row?.subscription_plan;

    // For CreateTaskListUnit, we need to check limits for all plans except paid plans with purchased seats
    if (action.actionName === ActionName.CreateTaskListUnit) {
        if (!action.seatUsage.locations.enforceLimit) {
            return true;
        }
        switch (clientSubscriptionPlan) {
            case SUBSCRIPTION_PLANS.FREE_TRIAL:
                return true;
            case SUBSCRIPTION_PLANS.FREEMIUM:
            case undefined:
            case null:
                return action.seatUsage.locations.current < FREEMIUM_LIMITS.LocationCount;
            case SUBSCRIPTION_PLANS.STANDARD:
            default: {
                return action.seatUsage.locations.current < action.seatUsage.locations.limit;
            }
        }
    }
    
    // For other actions, only apply limits to freemium users
    if (clientSubscriptionPlan !== SUBSCRIPTION_PLANS.FREEMIUM) {
        return true;
    }

    const currentClient = dsManager.clientAR!.row!;
    const usage = currentClient.meta
    switch (action.actionName) {
        case ActionName.CreateItem:
            return usage.itemsCount < FREEMIUM_LIMITS.ItemCount;
        case ActionName.ViewHistory:
            const timestamp = parseInt(action.snapshot.docIdAndTime.split('#')[1])
            return !isMoreThanNDaysAgo(new Date(timestamp), FREEMIUM_LIMITS.HistoryFreeDays);
        case ActionName.CreateQuiz:
            return usage.quizzesCount < FREEMIUM_LIMITS.QuizCount;
        case ActionName.AiGenerate:
            return usage.aiUsageCount < FREEMIUM_LIMITS.AiQuizGenerationCount;
        default:
            return true;
    }
}
